import { Injectable, Type } from '@angular/core';
import { AccessPoint } from '@kinet-open-network/client/access-points/data-access';
import {
  apiFilter,
  ApiPaginatedResponse,
  ApiQuery,
  ApiResponse,
  BaseApiService,
} from '@kinet-open-network/client/core/api-client';
import {
  downloadBlob,
  getFilenameFromHeaders,
} from '@kinet-open-network/client/shared/util';
import { Task } from '@kinet-open-network/client/tasks/data-access';
import {
  CustomerActivitySource,
  ICustomerActionData,
  ICustomerActivity,
  ICustomerBalance,
  ICustomerBatchActionData,
  ICustomerFee,
  ICustomerStoryLog,
  TaskType,
} from '@kinet-open-network/shared/data-access/model';
import { plainToInstance } from 'class-transformer';
import { endOfMinute, formatISO } from 'date-fns';
import { firstValueFrom, map, Observable } from 'rxjs';
import { MoveCustomerFeeData } from '../models/api/move-fee.model';
import {
  MoveServicesData,
  MoveServicesResult,
} from '../models/api/move-services.model';
import { SetAccessPointCloseDate } from '../models/api/set-close-date.model';
import { SetInvoiceBalanceData } from '../models/api/set-invoice-balance.model';
import { SetSipoInvoiceStateData } from '../models/api/set-sipo-invoice-state.model';
import { TransferServicesData } from '../models/api/transfer-services.model';
import { CustomerActivity } from '../models/customer-activity.model';
import { CustomerBalance } from '../models/customer-balance.model';
import { CustomerFee } from '../models/customer-fee.model';
import { Customer } from '../models/customer.model';

@Injectable({
  providedIn: 'root',
})
export class CustomersService extends BaseApiService<Customer> {
  override model: Type<Customer> = Customer;

  override get endpoint(): string {
    return '/customers';
  }

  override findOne(id: number): Observable<Customer> {
    return this.http
      .get<ApiResponse<Customer>>(`${this.endpoint}/${this.encodeId(id)}`, {
        params: { withDeleted: true },
      })
      .pipe(map((data) => plainToInstance(Customer, data.data)));
  }

  restore(id: number): Observable<Customer> {
    return this.http
      .put<
        ApiResponse<Customer>
      >(`${this.endpoint}/${this.encodeId(id)}/restore`, {})
      .pipe(map((data) => plainToInstance(Customer, data.data)));
  }

  getAccessPoints(customerId: number) {
    return this.http
      .get<
        ApiPaginatedResponse<AccessPoint>
      >(`${this.endpoint}/${customerId}/access-points`)
      .pipe(map((data) => plainToInstance(AccessPoint, data.data)));
  }

  bindAccessPoint(customerId: number, apId: string) {
    return this.http
      .put<Customer>(`${this.endpoint}/${customerId}/bind-access-point`, {
        apId,
      })
      .pipe(map((customer) => plainToInstance(Customer, customer)));
  }

  closeAccessPoint(
    customerId: number,
    apId: string,
    data: SetAccessPointCloseDate,
  ) {
    return this.http
      .put<Customer>(`${this.endpoint}/${customerId}/close-access-point`, {
        apId,
        ...data,
      })
      .pipe(map((customer) => plainToInstance(Customer, customer)));
  }

  unbindAccessPoint(customerId: number, apId: string) {
    return this.http
      .put<Customer>(`${this.endpoint}/${customerId}/unbind-access-point`, {
        apId,
      })
      .pipe(map((customer) => plainToInstance(Customer, customer)));
  }

  deleteAccessPoint(customerId: number, apId: string) {
    return this.http
      .put<Customer>(`${this.endpoint}/${customerId}/delete-access-point`, {
        apId,
      })
      .pipe(map((customer) => plainToInstance(Customer, customer)));
  }

  changeAccessPoint(id: number, apId: string, newApId: string) {
    return this.http
      .put<Customer>(`${this.endpoint}/${id}/change-access-point`, {
        apId,
        newApId,
      })
      .pipe(map((customer) => plainToInstance(Customer, customer)));
  }

  getInstallations(customerId: number, apId: string): Observable<Task[]> {
    return this.http
      .get<ApiPaginatedResponse<Task>>(`/tasks`, {
        params: this.apiQueryToHttpParams({
          filters: apiFilter<Task>()
            .customerId(customerId)
            .apId(apId)
            .type(TaskType.installation),
          sort: ['-createdAt'], // sort desc
        }),
      })
      .pipe(map((data) => plainToInstance(Task, data.data)));
  }

  async exportServices(
    query?: ApiQuery | null,
    filename?: string,
  ): Promise<void> {
    const response = await firstValueFrom(
      this.http.get(`${this.endpoint}/export/services`, {
        params: this.apiQueryToHttpParams(query),
        responseType: 'blob',
        observe: 'response',
      }),
    );

    const blob = response.body as Blob;

    if (filename === undefined) {
      filename = getFilenameFromHeaders(response.headers);
    }

    return downloadBlob(
      blob,
      filename ?? `export-services-${new Date().getTime()}.csv`,
    );
  }

  async exportDevices(
    query?: ApiQuery | null,
    filename?: string,
  ): Promise<void> {
    const response = await firstValueFrom(
      this.http.get(`${this.endpoint}/export/devices`, {
        params: this.apiQueryToHttpParams(query),
        responseType: 'blob',
        observe: 'response',
      }),
    );

    const blob = response.body as Blob;

    if (filename === undefined) {
      filename = getFilenameFromHeaders(response.headers);
    }

    return downloadBlob(
      blob,
      filename ?? `export-devices-${new Date().getTime()}.csv`,
    );
  }

  async exportFees(query?: ApiQuery | null, filename?: string): Promise<void> {
    const response = await firstValueFrom(
      this.http.get(`${this.endpoint}/export/fees`, {
        params: this.apiQueryToHttpParams(query),
        responseType: 'blob',
        observe: 'response',
      }),
    );

    const blob = response.body as Blob;

    if (filename === undefined) {
      filename = getFilenameFromHeaders(response.headers);
    }

    return downloadBlob(
      blob,
      filename ?? `export-fees-${new Date().getTime()}.csv`,
    );
  }

  async exportMaterials(
    query?: ApiQuery | null,
    filename?: string,
  ): Promise<void> {
    const response = await firstValueFrom(
      this.http.get(`${this.endpoint}/export/materials`, {
        params: this.apiQueryToHttpParams(query),
        responseType: 'blob',
        observe: 'response',
      }),
    );

    const blob = response.body as Blob;

    if (filename === undefined) {
      filename = getFilenameFromHeaders(response.headers);
    }

    return downloadBlob(
      blob,
      filename ?? `export-materials-${new Date().getTime()}.csv`,
    );
  }

  async exportAdditionalProducts(
    query?: ApiQuery | null,
    filename?: string,
  ): Promise<void> {
    const response = await firstValueFrom(
      this.http.get(`${this.endpoint}/export/additional-products`, {
        params: this.apiQueryToHttpParams(query),
        responseType: 'blob',
        observe: 'response',
      }),
    );

    const blob = response.body as Blob;

    if (filename === undefined) {
      filename = getFilenameFromHeaders(response.headers);
    }

    return downloadBlob(
      blob,
      filename ?? `export-additional-products-${new Date().getTime()}.csv`,
    );
  }

  batchAction(data: ICustomerBatchActionData, query?: ApiQuery | null) {
    return this.http.post<{ total: number }>(
      `${this.endpoint}/batch-action`,
      data,
      {
        params: this.apiQueryToHttpParams(query),
      },
    );
  }

  action(customerId: number, data: Partial<ICustomerActionData>) {
    return this.http.post<{ jobId: string }>(
      `${this.endpoint}/${customerId}/action`,
      data,
    );
  }

  bindTag(customerId: number, tagId: string) {
    return this.http
      .put<Customer>(`${this.endpoint}/${customerId}/tag/${tagId}/bind`, {})
      .pipe(map((customer) => plainToInstance(Customer, customer)));
  }

  unbindTag(customerId: number, tagId: string) {
    return this.http
      .put<Customer>(`${this.endpoint}/${customerId}/tag/${tagId}/unbind`, {})
      .pipe(map((customer) => plainToInstance(Customer, customer)));
  }

  getStory(customerId: number) {
    return this.http.get<ICustomerStoryLog[]>(
      `${this.endpoint}/${customerId}/story`,
    );
  }

  changeCustomerFeeApId(id: string, data: MoveCustomerFeeData) {
    return this.http
      .put<ICustomerFee>(`${this.endpoint}/fees/${id}/change-ap`, data)
      .pipe(map((fee) => plainToInstance(CustomerFee, fee)));
  }

  moveServicesToAnotherAp(
    id: number,
    apId: string,
    newApId: string,
    serviceId?: string,
  ) {
    return this.http.put<boolean>(
      `${this.endpoint}/${id}/move-services-to-another-ap`,
      {
        apId,
        newApId,
        serviceId,
      },
    );
  }

  moveServices(customerId: number, data: MoveServicesData) {
    return this.http.put<MoveServicesResult>(
      `${this.endpoint}/${customerId}/move-services`,
      data,
    );
  }

  transferServices(customerId: number, data: TransferServicesData) {
    return this.http.put<MoveServicesResult>(
      `${this.endpoint}/${customerId}/transfer-services`,
      data,
    );
  }

  setInvoiceBalance(customerId: number, data: SetInvoiceBalanceData) {
    return this.http.put<ICustomerBalance>(
      `${this.endpoint}/${customerId}/invoicing/balance`,
      {
        ...data,
      },
    );
  }

  getInvoiceBalance(customerId: number, date: Date) {
    return this.http
      .get<{ data: ICustomerBalance }>(
        `${this.endpoint}/${customerId}/invoicing/balance`,
        {
          params: {
            date: endOfMinute(date).toJSON(),
          },
        },
      )
      .pipe(map((res) => plainToInstance(CustomerBalance, res.data)));
  }

  setInvoiceFrom(customerId: number, date: Date | null) {
    return this.http
      .put<{ data: Customer }>(
        `${this.endpoint}/${customerId}/invoicing/defer`,
        {
          invoiceFrom:
            date !== null ? formatISO(date, { representation: 'date' }) : null,
        },
      )
      .pipe(map(({ data: customer }) => plainToInstance(Customer, customer)));
  }

  setSipoInvoicingState(customerId: number, data: SetSipoInvoiceStateData) {
    return this.http
      .put<{ data: Customer }>(
        `${this.endpoint}/${customerId}/invoicing/sipo`,
        {
          sipoInvoiceFrom:
            data.sipoInvoiceFrom !== null
              ? formatISO(data.sipoInvoiceFrom, { representation: 'date' })
              : null,
        },
      )
      .pipe(map(({ data: customer }) => plainToInstance(Customer, customer)));
  }

  getActivities(
    customerId: number,
    source: CustomerActivitySource,
    query?: ApiQuery,
  ): Observable<ApiPaginatedResponse<CustomerActivity>> {
    return this.http
      .get<ApiPaginatedResponse<ICustomerActivity>>(
        `${this.endpoint}/${customerId}/activities/${source}`,
        {
          params: this.apiQueryToHttpParams({
            ...query,
            perPage: -1, // load all activities without pagination
          }),
        },
      )
      .pipe(
        map((res) => ({
          ...res,
          data: plainToInstance(CustomerActivity, res.data),
        })),
      );
  }
}
